import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col justify-center px-6 sm:px-10 md:px-16 pt-32 pb-24 sm:pt-40 sm:pb-32"
    >
      <div className="max-w-7xl mx-auto w-full">
        {/* Overline */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={0}
          variants={fadeUp}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-blue-500/70" />
          <span
            className="text-[11px] sm:text-xs font-bold tracking-[0.32em] uppercase text-white/70 font-heading"
            data-testid="hero-overline"
          >
            Prémium Webfejlesztés
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial="hidden"
          animate="show"
          custom={1}
          variants={fadeUp}
          data-testid="hero-headline"
          className="font-heading text-white font-light tracking-tighter leading-[1.02] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] max-w-5xl"
        >
          Digitális jelenlét,{" "}
          <span className="italic font-serif text-blue-400">kompromisszumok</span>{" "}
          nélkül.
        </motion.h1>

        {/* Description */}
        <motion.p
          initial="hidden"
          animate="show"
          custom={2}
          variants={fadeUp}
          data-testid="hero-description"
          className="mt-10 max-w-2xl text-base sm:text-lg md:text-xl text-white/70 font-light leading-relaxed"
        >
          Modern, reszponzív és prémium kategóriás weboldalakat tervezek és
          fejlesztek. Emelje új szintre vállalkozását egy olyan digitális
          arculattal, amely eleganciát sugároz és valódi eredményeket hoz.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={3}
          variants={fadeUp}
          className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center"
        >
          <a
            href="#kapcsolat"
            data-testid="hero-contact-button"
            className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white text-[#030712] text-sm font-semibold tracking-wider uppercase overflow-hidden transition-transform duration-500 hover:scale-[1.02]"
          >
            <span className="relative z-10">Vegyük fel a kapcsolatot</span>
            <ArrowUpRight
              className="relative z-10 w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              strokeWidth={2}
            />
            <span className="absolute inset-0 bg-blue-500 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            <span className="absolute inset-0 mix-blend-difference" />
          </a>

          <a
            href="#kapcsolat"
            data-testid="hero-secondary-link"
            className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-white/70 hover:text-white transition-colors duration-300"
          >
            <Sparkles className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
            Ingyenes konzultáció
          </a>
        </motion.div>

        {/* Value pillars */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={4}
          variants={fadeUp}
          className="mt-24 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden"
        >
          {[
            {
              k: "01",
              t: "Egyedi Tervezés",
              d: "Minden pixel az Ön márkáját szolgálja — sablonok nélkül, prémium minőségben.",
            },
            {
              k: "02",
              t: "Villámgyors Élmény",
              d: "Optimalizált teljesítmény, amely minden eszközön kifogástalanul működik.",
            },
            {
              k: "03",
              t: "Hosszú Távú Partnerség",
              d: "Folyamatos támogatás és iteráció, hogy a weboldala mindig az élen járjon.",
            },
          ].map((item) => (
            <div
              key={item.k}
              className="bg-[#0A1229]/80 p-8 sm:p-10 group hover:bg-[#0A1229] transition-colors duration-500"
              data-testid={`hero-pillar-${item.k}`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs tracking-[0.3em] text-blue-400 font-mono">
                  {item.k}
                </span>
                <span className="h-px w-8 bg-white/20 group-hover:w-16 group-hover:bg-blue-400 transition-all duration-500" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl text-white font-light tracking-tight mb-3">
                {item.t}
              </h3>
              <p className="text-sm text-white/60 font-light leading-relaxed">
                {item.d}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
